import { useState } from "react";
import QuestionLayout from "./QuestionLayout";
import { renderStaffLines, renderClef } from "./staff/staffRender";
import {
  snapYToPitch,
  pitchToY
} from "./staff/pitchMath";
import { Glyph } from "./staff/glyphs.jsx";

/* ───────── CONSTANTS ───────── */

const STAFF_TOP = 40;
const STAFF_HEIGHT = 100;

const LEFT_MARGIN = 100;
const SLOT_WIDTH = 140;

const BARLINE_HEIGHT = 40;
const BARLINE_Y = STAFF_TOP;

const MAX_ACCIDENTALS = 7;
const ACCIDENTAL_SPACING = 12;
const ACCIDENTAL_START_X = 20;

/* ───────── COMPONENT ───────── */

export default function KeySignatureInputWidget({
  config,
  value = {},
  onChange,
  mode = "edit"
}) {
  const {
    number,
    prompt,
    points,
    clef,
    slots,
    allowLedgerLines = true
  } = config;

  const staffWidth = LEFT_MARGIN + slots * SLOT_WIDTH;
  const accidentalsBySlot = value.accidentals || {};

  // Used only for the desktop mouse/trackpad preview.
  const [hover, setHover] = useState(null);

  // null | "#" | "b"
  const [activeAccidental, setActiveAccidental] = useState(null);

  function getPointerPitch(event) {
    const svg = event.currentTarget.ownerSVGElement;
    if (!svg) return null;

    const screenCTM = svg.getScreenCTM();
    if (!screenCTM) return null;

    const point = svg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;

    const svgPoint = point.matrixTransform(screenCTM.inverse());

    return snapYToPitch(svgPoint.y, clef, {
      allowLedgerLines
    });
  }

  function placeAccidental(slot, pitch) {
    if (!activeAccidental) return;
    if (!pitch) return;

    const existing = accidentalsBySlot[slot] || [];

    if (existing.length >= MAX_ACCIDENTALS) return;

    onChange({
      ...value,
      accidentals: {
        ...accidentalsBySlot,
        [slot]: [
          ...existing,
          {
            pitch,
            accidental: activeAccidental
          }
        ]
      }
    });
  }

  function removeAccidental(slot, index) {
    const existing = accidentalsBySlot[slot] || [];
    const next = existing.filter((_, i) => i !== index);

    onChange({
      ...value,
      accidentals: {
        ...accidentalsBySlot,
        [slot]: next
      }
    });
  }

  return (
    <QuestionLayout
      number={number}
      prompt={prompt}
      points={points}
    >
      <div>
        {/* ───── PALETTE ───── */}
        {mode === "edit" && (
          <div style={{ marginBottom: 10 }}>
            <button
              type="button"
              onClick={() => {
                setActiveAccidental("b");
                setHover(null);
              }}
              style={{
                marginRight: 6,
                border:
                  activeAccidental === "b"
                    ? "2px solid #1976d2"
                    : undefined
              }}
            >
              ♭ Flat
            </button>

            <button
              type="button"
              onClick={() => {
                setActiveAccidental("#");
                setHover(null);
              }}
              style={{
                border:
                  activeAccidental === "#"
                    ? "2px solid #1976d2"
                    : undefined
              }}
            >
              ♯ Sharp
            </button>
          </div>
        )}

        <svg
          width={staffWidth + 40}
          height={140}
          style={{
            /*
             * Prevent iPad Safari from converting a staff tap into
             * scrolling or synthetic mouse-hover behavior.
             */
            touchAction: mode === "edit" ? "none" : "auto",
            userSelect: "none",
            WebkitUserSelect: "none"
          }}
        >
          {renderStaffLines(staffWidth)}
          {renderClef(clef)}

          {/* ───── SLOTS ───── */}
          {Array.from({ length: slots }).map((_, i) => {
            const slot = i + 1;
            const x = LEFT_MARGIN + i * SLOT_WIDTH - 20;
            const placed = accidentalsBySlot[slot] || [];

            return (
              <g key={slot}>
                {/* HIT AREA */}
                <rect
                  x={x}
                  y={STAFF_TOP - 40}
                  width={SLOT_WIDTH}
                  height={STAFF_HEIGHT + 60}
                  fill="transparent"
                  opacity={1}
                  style={{
                    cursor:
                      mode === "edit" && activeAccidental
                        ? "crosshair"
                        : "default"
                  }}
                  onPointerMove={(event) => {
                    if (mode !== "edit") return;
                    if (!activeAccidental) return;

                    /*
                     * A touchscreen has no persistent hover state.
                     * Do not generate a ghost position from touch movement.
                     */
                    if (event.pointerType === "touch") return;

                    const pitch = getPointerPitch(event);
                    if (!pitch) return;

                    setHover({
                      slot,
                      pitch
                    });
                  }}
                  onPointerLeave={() => {
                    setHover(null);
                  }}
                  onPointerDown={(event) => {
                    if (mode !== "edit") return;
                    if (!activeAccidental) return;

                    /*
                     * Ignore right-click and other secondary mouse buttons.
                     */
                    if (
                      event.pointerType === "mouse" &&
                      event.button !== 0
                    ) {
                      return;
                    }

                    event.preventDefault();
                    event.stopPropagation();

                    /*
                     * Calculate the pitch from this exact tap/click.
                     * Never place using hover.pitch.
                     */
                    const pitch = getPointerPitch(event);
                    if (!pitch) return;

                    placeAccidental(slot, pitch);

                    /*
                     * Clear the preview so iPad cannot carry a queued
                     * location into the next tap.
                     */
                    setHover(null);
                  }}
                />

                {/* PLACED ACCIDENTALS */}
                {placed.map((accidental, index) => (
                  <g
                    key={`${accidental.pitch}-${accidental.accidental}-${index}`}
                    transform={`translate(
                      ${
                        x +
                        ACCIDENTAL_START_X +
                        index * ACCIDENTAL_SPACING
                      },
                      ${pitchToY(accidental.pitch, clef)}
                    )`}
                    style={{
                      cursor:
                        mode === "edit"
                          ? "pointer"
                          : "default"
                    }}
                    onPointerDown={(event) => {
                      if (mode !== "edit") return;

                      if (
                        event.pointerType === "mouse" &&
                        event.button !== 0
                      ) {
                        return;
                      }

                      event.preventDefault();
                      event.stopPropagation();

                      removeAccidental(slot, index);
                      setHover(null);
                    }}
                  >
                    {/* HIT BOX */}
                    <rect
                      x={-12}
                      y={-14}
                      width={16}
                      height={28}
                      fill="transparent"
                      pointerEvents="all"
                    />

                    <Glyph
                      symbol="quarter_note"
                      accidental={accidental.accidental}
                      accidentalOnly
                    />
                  </g>
                ))}

                {/* HOVER GHOST — DESKTOP MOUSE/TRACKPAD ONLY */}
                {hover?.slot === slot &&
                  activeAccidental &&
                  placed.length < MAX_ACCIDENTALS && (
                    <g
                      transform={`translate(
                        ${
                          x +
                          ACCIDENTAL_START_X +
                          placed.length * ACCIDENTAL_SPACING
                        },
                        ${pitchToY(hover.pitch, clef)}
                      )`}
                      opacity={0.4}
                      pointerEvents="none"
                    >
                      <Glyph
                        symbol="quarter_note"
                        accidental={activeAccidental}
                        accidentalOnly
                        fill="#999"
                      />
                    </g>
                  )}

                {/* KEY SIGNATURE PROMPTS */}
                {config.keyPrompts?.[slot]?.length > 0 && (
                  <text
                    x={x + SLOT_WIDTH / 2 + 5}
                    y={STAFF_TOP + STAFF_HEIGHT + 30}
                    textAnchor="middle"
                    fontSize="16"
                    fontWeight={600}
                    fill="#000"
                    pointerEvents="none"
                  >
                    {config.keyPrompts[slot].join(" – ")}
                  </text>
                )}

                {/* SINGLE BARLINE */}
                {slot < slots && (
                  <rect
                    x={x + SLOT_WIDTH}
                    y={BARLINE_Y}
                    width={1}
                    height={BARLINE_HEIGHT}
                    fill="#000"
                    pointerEvents="none"
                  />
                )}
              </g>
            );
          })}

          {/* DOUBLE BARLINE */}
          <>
            <rect
              x={LEFT_MARGIN + slots * SLOT_WIDTH - 6}
              y={BARLINE_Y}
              width={1}
              height={BARLINE_HEIGHT}
              fill="#000"
              pointerEvents="none"
            />

            <rect
              x={LEFT_MARGIN + slots * SLOT_WIDTH - 3}
              y={BARLINE_Y}
              width={3}
              height={BARLINE_HEIGHT}
              fill="#000"
              pointerEvents="none"
            />
          </>
        </svg>
      </div>
    </QuestionLayout>
  );
}